import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Mail, Key, Loader2, CheckCircle2, Shield, ArrowRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export const FirstLoginSetupPage = () => {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: email, 2: verify OTP, 3: password
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  const otpRefs = useRef([]);

  // Timer for resend button
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSendOTP = async () => {
    if (!email) {
      toast.error('Inserisci un indirizzo email');
      return;
    }
    
    if (!/^[\w.-]+@[\w.-]+\.\w+$/.test(email)) {
      toast.error('Formato email non valido');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/setup/send-otp', { email });
      toast.success('Codice inviato! Controlla la tua email');
      setStep(2);
      setResendTimer(60); // 60 seconds cooldown
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio del codice');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only digits
    
    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1); // Take only last character
    setOtpCode(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otpCode];
    for (let i = 0; i < paste.length; i++) {
      newOtp[i] = paste[i];
    }
    setOtpCode(newOtp);
    // Focus last filled or next empty
    const lastIndex = Math.min(paste.length, 5);
    otpRefs.current[lastIndex]?.focus();
  };

  const handleVerifyAndComplete = async () => {
    const code = otpCode.join('');
    
    if (code.length !== 6) {
      toast.error('Inserisci il codice completo a 6 cifre');
      return;
    }
    
    if (!newPassword) {
      toast.error('Inserisci una nuova password');
      return;
    }
    
    if (newPassword.length < 4) {
      toast.error('La password deve avere almeno 4 caratteri');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/setup/verify', {
        email,
        otp_code: code,
        new_password: newPassword
      });
      toast.success('Configurazione completata! Benvenuto in Servicare');
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella verifica');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    await handleSendOTP();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">
            Completa la Registrazione
          </CardTitle>
          <CardDescription className="text-slate-500">
            Benvenuto {user?.username}! Configura il tuo account per continuare.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Progress steps */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-sky-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
              1
            </div>
            <div className={`w-12 h-1 ${step >= 2 ? 'bg-sky-600' : 'bg-slate-200'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-sky-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
              2
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-sky-600" />
                  Indirizzo Email
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@esempio.com"
                  className="h-12 text-base"
                  data-testid="setup-email-input"
                />
                <p className="text-xs text-slate-400">
                  Riceverai un codice di verifica a questo indirizzo
                </p>
              </div>
              
              <Button
                onClick={handleSendOTP}
                disabled={loading}
                className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-base"
                data-testid="send-otp-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Invia Codice
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {/* OTP Input */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 text-center block">
                  Inserisci il codice a 6 cifre
                </Label>
                <p className="text-xs text-slate-400 text-center mb-4">
                  Inviato a <span className="font-medium text-slate-600">{email}</span>
                </p>
                <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                  {otpCode.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => otpRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-bold"
                      data-testid={`otp-input-${index}`}
                    />
                  ))}
                </div>
                <button
                  onClick={handleResendOTP}
                  disabled={resendTimer > 0 || loading}
                  className="w-full text-sm text-sky-600 hover:text-sky-700 mt-2 flex items-center justify-center gap-1"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  {resendTimer > 0 ? `Reinvia tra ${resendTimer}s` : 'Reinvia codice'}
                </button>
              </div>

              {/* Password fields */}
              <div className="space-y-3 pt-4 border-t">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Key className="w-4 h-4 text-sky-600" />
                    Nuova Password
                  </Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimo 4 caratteri"
                    className="h-12"
                    data-testid="setup-password-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Conferma Password
                  </Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ripeti la password"
                    className="h-12"
                    data-testid="setup-confirm-password-input"
                  />
                </div>
              </div>

              <Button
                onClick={handleVerifyAndComplete}
                disabled={loading}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base"
                data-testid="complete-setup-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Completa Registrazione
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
